/**
 * Correlation ids and the X-BONO-* headers auth-api already understands.
 *
 * The backend reads these in LoggingServiceImpl.getRequestMetadata and OperationContextUtil,
 * and messaging-util puts the trace id on ret_event_record.correlation_id. Today the SPA sends
 * none of them, so every request mints a fresh server-side id and nothing correlates across the
 * email-lookup -> sign-in -> exchange chain.
 *
 *   traceId    (X-BONO-TRACE-ID)     one browser tab's whole auth journey
 *   attemptId  (X-BONO-OPERATION-ID) one user submit action
 */

const SESSION_KEY = 'bono.auth.sid';

let attemptId: string = randomId();

/**
 * crypto.randomUUID() needs a secure context, which excludes a bare-IP dev host over http.
 * The reporter must never be the thing that throws, so fall back rather than assume.
 */
function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable for the tab. sessionStorage so a reload keeps it but a new tab starts fresh. */
export function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      return existing;
    }
    const created = randomId();
    sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    // Safari private mode / blocked storage: still return something usable.
    return attemptId;
  }
}

export function getTraceId(): string {
  return getSessionId();
}

export function getAttemptId(): string {
  return attemptId;
}

/** Call at the top of each submit handler so one attempt's failures group together. */
export function newAttempt(): string {
  attemptId = randomId();
  return attemptId;
}

/**
 * All of these are already consumed by auth-api (see HeaderDTO). Note there is deliberately no
 * X-BONO-USER-EMAIL: the email travels in the validated request body instead, so there is
 * exactly one place in the system that has to reason about it.
 */
export function getTraceHeaders(operation?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'X-BONO-TRACE-ID': getTraceId(),
    'X-BONO-OPERATION-ID': getAttemptId(),
    'X-BONO-OP-CHAIN': 'bono-auth',
    'X-BONO-APPLICATION': 'BONO_AUTH',
  };
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      headers['X-BONO-ORIGIN'] = window.location.origin;
    }
  } catch {
    /* ignore */
  }
  if (operation) {
    headers['X-BONO-OPERATION'] = operation;
  }
  return headers;
}
